import { useState, useEffect, useRef, useCallback } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';

const INTERVAL = 6000;
const FADE = 'opacity 800ms cubic-bezier(0.16, 1, 0.3, 1)';

const CODE_TEXT = `# Bayesian updating — FIB 34 carbonation model
import numpy as np
from scipy import stats

# Prior: chloride threshold (Beta distribution)
alpha_prior, beta_prior = 2.5, 4.0
prior = stats.beta(alpha_prior, beta_prior)

# Likelihood from field inspection data
observed_data = [0.42, 0.38, 0.51, 0.45, 0.39]

# Posterior update
alpha_post = alpha_prior + sum(observed_data)
beta_post  = beta_prior  + len(observed_data)
posterior  = stats.beta(alpha_post, beta_post)

print(f"Posterior mean: {posterior.mean():.3f}")`;

const USER_MSG = 'How do I specify low-carbon concrete for a residential project in a temperate coastal climate?';
const AI_MSG = 'For a temperate coastal residential project, I recommend targeting a Portland cement replacement of 40\u201350% using GGBS or PFA. Specify to BS EN 206 with a minimum 28-day strength of C30/37 and ensure GGBS is sourced from certified suppliers. Embodied carbon target: \u2264 200 kgCO\u2082e/m\u00B3.';
const AI_WORDS = AI_MSG.split(' ');

/* ───────── Slide 3: Code Animation ───────── */
export const CodeSlide = ({ isActive }) => {
  const [charIdx, setCharIdx] = useState(0);
  const [chartOpacity, setChartOpacity] = useState(0);
  const canvasRef = useRef(null);
  const timerRef = useRef(null);

  useEffect(() => {
    if (!isActive) { setCharIdx(0); setChartOpacity(0); return; }
    let i = 0;
    const total = CODE_TEXT.length;
    const ms = Math.max(2, 2500 / total);
    timerRef.current = setInterval(() => {
      i += 1;
      if (i >= total) { clearInterval(timerRef.current); setCharIdx(total); setTimeout(() => setChartOpacity(1), 200); return; }
      setCharIdx(i);
    }, ms);
    return () => clearInterval(timerRef.current);
  }, [isActive]);

  useEffect(() => {
    if (chartOpacity === 0 || !canvasRef.current) return;
    const c = canvasRef.current;
    const ctx = c.getContext('2d');
    const w = c.width, h = c.height;
    ctx.clearRect(0, 0, w, h);
    ctx.strokeStyle = 'rgba(255,255,255,0.25)'; ctx.lineWidth = 1;
    ctx.beginPath(); ctx.moveTo(40, h - 28); ctx.lineTo(w - 10, h - 28); ctx.stroke();
    ctx.beginPath(); ctx.moveTo(40, h - 28); ctx.lineTo(40, 8); ctx.stroke();
    ctx.fillStyle = 'rgba(255,255,255,0.45)'; ctx.font = '10px sans-serif';
    ctx.fillText('Chloride threshold (% by weight)', w / 2 - 90, h - 4);
    const gauss = (x, mu, sig) => Math.exp(-0.5 * ((x - mu) / sig) ** 2);
    ctx.beginPath(); ctx.strokeStyle = '#6272a4'; ctx.lineWidth = 2;
    for (let px = 40; px < w - 10; px++) { const v = gauss(px, w * 0.42, 80) * (h - 50); ctx.lineTo(px, h - 28 - v); }
    ctx.stroke();
    ctx.beginPath(); ctx.strokeStyle = '#ffb86c'; ctx.lineWidth = 2;
    for (let px = 40; px < w - 10; px++) { const v = gauss(px, w * 0.52, 40) * (h - 36); ctx.lineTo(px, h - 28 - v); }
    ctx.stroke();
    ctx.fillStyle = '#6272a4'; ctx.font = '11px sans-serif'; ctx.fillText('\u25CF Prior distribution', 55, 18);
    ctx.fillStyle = '#ffb86c'; ctx.fillText('\u25CF Posterior (updated)', 55, 34);
  }, [chartOpacity]);

  return (
    <div className="absolute inset-0 overflow-hidden" style={{ background: '#1e1e2e' }}>
      <div className="absolute inset-x-4 bottom-4 md:bottom-auto md:inset-x-auto md:right-12 lg:right-24 md:top-16 max-w-full md:max-w-[55%] lg:max-w-lg opacity-70" style={{ transform: 'scale(0.8)', transformOrigin: 'top right' }}>
        <pre className="text-[9px] sm:text-xs md:text-sm leading-relaxed overflow-hidden whitespace-pre-wrap" style={{ color: '#50fa7b', fontFamily: "'Fira Code','Courier New',monospace" }}>
          {CODE_TEXT.substring(0, charIdx)}
          {charIdx < CODE_TEXT.length && <span className="animate-pulse">\u258E</span>}
        </pre>
        <canvas ref={canvasRef} width={520} height={180} className="mt-3 w-full max-w-[520px]" style={{ opacity: chartOpacity, transition: 'opacity 0.6s ease' }} />
      </div>
    </div>
  );
};

/* ───────── Slide 6: Chat Animation ───────── */
export const ChatSlide = ({ isActive }) => {
  const [phase, setPhase] = useState(0);
  const [userChars, setUserChars] = useState(0);
  const [aiWords, setAiWords] = useState(0);
  const timersRef = useRef([]);

  const clear = () => { timersRef.current.forEach(clearTimeout); timersRef.current.forEach(clearInterval); timersRef.current = []; };

  useEffect(() => {
    if (!isActive) { clear(); setPhase(0); setUserChars(0); setAiWords(0); return; }
    setPhase(1);
    const t1 = setTimeout(() => {
      setPhase(2);
      let i = 0;
      const ms = 1500 / USER_MSG.length;
      const iv = setInterval(() => { i++; setUserChars(i); if (i >= USER_MSG.length) clearInterval(iv); }, ms);
      timersRef.current.push(iv);
    }, 1000);
    timersRef.current.push(t1);
    const t2 = setTimeout(() => {
      setPhase(3);
      let w = 0;
      const ms = 3000 / AI_WORDS.length;
      const iv = setInterval(() => { w++; setAiWords(w); if (w >= AI_WORDS.length) { clearInterval(iv); setPhase(4); } }, ms);
      timersRef.current.push(iv);
    }, 2500);
    timersRef.current.push(t2);
    return clear;
  }, [isActive]);

  return (
    <div className="absolute inset-0 overflow-hidden" style={{ background: '#171614' }}>
      <div className="absolute right-4 sm:right-12 lg:right-24 top-1/2 -translate-y-1/2 w-full max-w-md rounded-lg overflow-hidden shadow-2xl" style={{ opacity: phase >= 1 ? 0.8 : 0, transition: 'opacity 0.5s ease', background: '#1e293b', border: '1px solid rgba(255,255,255,0.08)' }}>
        <div className="flex items-center gap-2 px-4 py-3" style={{ borderBottom: '1px solid rgba(255,255,255,0.08)' }}>
          <span className="w-2 h-2 rounded-full bg-green-500 animate-pulse" />
          <span className="text-xs sm:text-sm font-medium text-white" style={{ fontFamily: 'Outfit,sans-serif' }}>DD Assistant &mdash; Concrete Specification AI</span>
        </div>
        <div className="p-4 space-y-3 min-h-[220px] sm:min-h-[260px] text-xs sm:text-sm">
          {phase >= 2 && (
            <div className="flex justify-end">
              <div className="max-w-[85%] px-3 py-2 rounded-lg text-white" style={{ background: '#15803d' }}>
                {USER_MSG.substring(0, userChars)}
                {userChars < USER_MSG.length && <span className="animate-pulse">\u258E</span>}
              </div>
            </div>
          )}
          {phase >= 3 && (
            <div className="flex justify-start">
              <div className="max-w-[85%] px-3 py-2 rounded-lg text-gray-200" style={{ background: '#334155' }}>
                {aiWords > 0 ? AI_WORDS.slice(0, aiWords).join(' ') : ''}
                {aiWords < AI_WORDS.length && aiWords > 0 && <span className="inline-block w-1.5 h-3.5 bg-green-400 ml-1 animate-pulse rounded-sm" />}
                {phase === 3 && aiWords === 0 && (
                  <span className="flex gap-1 py-1">
                    <span className="w-1.5 h-1.5 rounded-full bg-gray-400 animate-bounce" style={{ animationDelay: '0ms' }} />
                    <span className="w-1.5 h-1.5 rounded-full bg-gray-400 animate-bounce" style={{ animationDelay: '150ms' }} />
                    <span className="w-1.5 h-1.5 rounded-full bg-gray-400 animate-bounce" style={{ animationDelay: '300ms' }} />
                  </span>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

/* ───────── Slide 7: Thermal FEA Animation ───────── */
const ThermalSlide = ({ isActive }) => {
  const canvasRef = useRef(null);
  const rafRef = useRef(null);

  useEffect(() => {
    if (!isActive) { if (rafRef.current) cancelAnimationFrame(rafRef.current); return; }
    if (!canvasRef.current) return;
    const c = canvasRef.current;
    const ctx = c.getContext('2d');
    const w = c.width, h = c.height;
    const start = performance.now();
    const draw = (now) => {
      const elapsed = (now - start) / 1000;
      const pulse = 0.6 + 0.4 * Math.sin(elapsed * Math.PI * 2 / 3);
      ctx.clearRect(0, 0, w, h);
      const cols = 16, rows = 12, ox = 40, oy = 55;
      const cw = (w - 80) / cols, ch = (h - 110) / rows;
      const cx = cols / 2, cy = rows / 2, maxD = Math.sqrt(cx * cx + cy * cy);
      for (let r = 0; r < rows; r++) {
        for (let col = 0; col < cols; col++) {
          const d = Math.sqrt((col - cx) ** 2 + (r - cy) ** 2) / maxD;
          let R, G, B;
          if (d < 0.5) { const f = d * 2; R = Math.round(230 - f * 60); G = Math.round(120 + f * 80); B = Math.round(30 + f * 30); }
          else { const f = (d - 0.5) * 2; R = Math.round(170 - f * 120); G = Math.round(200 - f * 30); B = Math.round(60 + f * 140); }
          ctx.fillStyle = `rgba(${R},${G},${B},${pulse})`;
          ctx.fillRect(ox + col * cw, oy + r * ch, cw, ch);
        }
      }
      ctx.strokeStyle = 'rgba(255,255,255,0.08)'; ctx.lineWidth = 0.5;
      for (let col = 0; col <= cols; col++) { ctx.beginPath(); ctx.moveTo(ox + col * cw, oy); ctx.lineTo(ox + col * cw, oy + rows * ch); ctx.stroke(); }
      for (let r = 0; r <= rows; r++) { ctx.beginPath(); ctx.moveTo(ox, oy + r * ch); ctx.lineTo(ox + cols * cw, oy + r * ch); ctx.stroke(); }
      ctx.fillStyle = 'rgba(255,255,255,0.6)'; ctx.font = '600 10px sans-serif';
      ctx.fillText('THERMAL FEA \u2014 HYDRATION MODEL', ox, 28);
      const ly = oy + rows * ch + 22;
      ctx.font = '10px sans-serif';
      ctx.fillStyle = 'rgba(255,255,255,0.5)'; ctx.fillText('Max \u0394T (core): 70\u00B0C', ox, ly);
      ctx.fillStyle = 'rgba(200,200,255,0.5)'; ctx.fillText('Max \u0394T (surface): 25\u00B0C', ox + 130, ly);
      rafRef.current = requestAnimationFrame(draw);
    };
    rafRef.current = requestAnimationFrame(draw);
    return () => { if (rafRef.current) cancelAnimationFrame(rafRef.current); };
  }, [isActive]);

  return (
    <div className="absolute inset-0 overflow-hidden flex flex-col md:flex-row">
      <div className="w-full md:w-1/2 h-1/2 md:h-full relative">
        <img src="https://images.pexels.com/photos/7931/pexels-photo-7931.jpg?auto=compress&cs=tinysrgb&w=1920" alt="Mass concrete construction with steel reinforcement and formwork" className="w-full h-full object-cover" loading="lazy" />
      </div>
      <div className="w-full md:w-1/2 h-1/2 md:h-full flex items-center justify-center" style={{ background: '#1e1e2e' }}>
        <canvas ref={canvasRef} width={500} height={340} className="max-w-full max-h-full" />
      </div>
    </div>
  );
};

/* ───────── Slide Config (7 slides) ───────── */
const SLIDES = [
  { type: 'image', src: 'https://images.unsplash.com/photo-1769389352425-bc3f5581f42b?w=1920&q=80', alt: 'Reinforced concrete bridge in aggressive coastal environment' },
  { type: 'component', Component: CodeSlide, overlay: 'directional' },
  { type: 'image', src: 'https://images.pexels.com/photos/5657436/pexels-photo-5657436.png?auto=compress&cs=tinysrgb&w=1920', alt: 'Application of polyurea waterproofing layer on concrete substrate' },
  { type: 'component', Component: ChatSlide },
  { type: 'component', Component: ThermalSlide },
  { type: 'image', src: 'https://images.unsplash.com/photo-1541959284-d104fd29a1a7?w=1920&q=80', alt: 'Large-scale data centre under construction' },
  { type: 'image', src: 'https://images.pexels.com/photos/6603943/pexels-photo-6603943.jpeg?auto=compress&cs=tinysrgb&w=1920', alt: 'Fresh asphalt concrete surface showing aggregate macrotexture' },
];

/* ───────── Main Slideshow ───────── */
export const HeroSlideshow = ({ children }) => {
  const [active, setActive] = useState(0);
  const [hovered, setHovered] = useState(false);
  const intervalRef = useRef(null);
  const touchRef = useRef(0);
  const reducedMotion = useRef(false);

  useEffect(() => { reducedMotion.current = window.matchMedia?.('(prefers-reduced-motion: reduce)')?.matches ?? false; }, []);
  const next = useCallback(() => setActive(i => (i + 1) % SLIDES.length), []);
  const prev = useCallback(() => setActive(i => (i - 1 + SLIDES.length) % SLIDES.length), []);

  useEffect(() => {
    if (reducedMotion.current || hovered) { clearInterval(intervalRef.current); return; }
    intervalRef.current = setInterval(next, INTERVAL);
    return () => clearInterval(intervalRef.current);
  }, [hovered, next]);

  useEffect(() => {
    const handler = (e) => { if (e.key === 'ArrowRight') next(); else if (e.key === 'ArrowLeft') prev(); };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [next, prev]);

  const onTouchStart = (e) => { touchRef.current = e.touches[0].clientX; };
  const onTouchEnd = (e) => { const d = e.changedTouches[0].clientX - touchRef.current; if (Math.abs(d) > 50) { d < 0 ? next() : prev(); } };
  const transStyle = reducedMotion.current ? 'none' : FADE;

  return (
    <section className="relative pt-16 min-h-[85vh] flex items-center overflow-hidden group/hero" data-testid="hero-section" onMouseEnter={() => setHovered(true)} onMouseLeave={() => setHovered(false)} onTouchStart={onTouchStart} onTouchEnd={onTouchEnd}>
      {SLIDES.map((slide, i) => (
        <div key={i} className="absolute inset-0" style={{ opacity: i === active ? 1 : 0, transition: transStyle, zIndex: i === active ? 1 : 0 }} aria-hidden={i !== active}>
          {slide.type === 'image' ? (
            <>
              <img src={slide.src} alt={slide.alt} className="w-full h-full object-cover" style={slide.overlay === 'neutral' ? { objectPosition: 'center center' } : undefined} loading={i === 0 ? 'eager' : 'lazy'} />
              {slide.overlay === 'neutral' ? (
                <div className="absolute inset-0" style={{ background: 'linear-gradient(to top, rgba(15,15,15,0.65) 0%, rgba(15,15,15,0.2) 50%, transparent 100%)' }} />
              ) : (
                <div className="absolute inset-0 hero-gradient-directional" />
              )}
            </>
          ) : (
            <>
              <slide.Component isActive={i === active} />
              {slide.overlay === 'directional' ? (
                <div className="absolute inset-0 pointer-events-none hero-gradient-directional" />
              ) : (
                <div className="absolute inset-0 bg-black/40 pointer-events-none" />
              )}
            </>
          )}
        </div>
      ))}
      <div className="relative z-[5] max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24 w-full">{children}</div>
      <div className="absolute bottom-6 left-1/2 -translate-x-1/2 z-[5] flex gap-2" data-testid="hero-dots">
        {SLIDES.map((_, i) => (
          <button key={i} onClick={() => setActive(i)} aria-label={`Go to slide ${i + 1}`} data-testid={`hero-dot-${i}`}
            className={`w-2 h-2 rounded-full transition-all duration-300 ${i === active ? 'bg-green-500 w-5' : 'bg-white/50 hover:bg-white/80'}`} />
        ))}
      </div>
      <button onClick={prev} aria-label="Previous slide" data-testid="hero-arrow-prev" className="absolute left-3 sm:left-6 top-1/2 -translate-y-1/2 z-[5] p-2 text-white/0 group-hover/hero:text-white/70 hover:!text-white focus:text-white transition-colors duration-300 outline-none">
        <ChevronLeft className="w-7 h-7" />
      </button>
      <button onClick={next} aria-label="Next slide" data-testid="hero-arrow-next" className="absolute right-3 sm:right-6 top-1/2 -translate-y-1/2 z-[5] p-2 text-white/0 group-hover/hero:text-white/70 hover:!text-white focus:text-white transition-colors duration-300 outline-none">
        <ChevronRight className="w-7 h-7" />
      </button>
    </section>
  );
};
